import { useEffect, useMemo, useRef } from "react";
import Skeleton from "react-loading-skeleton";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import "./headset-logo.input.scss";

const normalizeFile = async (file) => {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    return new File([blob], file.name, { type: "image/png" });
  } catch {
    return file;
  }
};

export const HeadsetLogoInput = ({
  value,
  onChange,
  loading,
  errors,
  hidden,
}) => {
  const inputRef = useRef();

  const handleClick = async () => {
    inputRef.current.click();
  };

  const handleFileChange = async ({ target }) => {
    const file = target.files[0];

    if (file) {
      // Canvas re-encodes the PNG to normalize compression,
      // some editors produce headers that trigger WAF rules.
      const normalizedFile = await normalizeFile(file);
      onChange?.(normalizedFile);
    }
  };

  const errorNotification = useMemo(() => {
    if (errors) {
      const content =
        Array.isArray(errors) && errors?.length > 0 ? (
          <>
            <ul>
              {errors.map((error) => (
                <li key={error.code}>{error?.message}</li>
              ))}
            </ul>
          </>
        ) : (
          <>{errors}</>
        );

      return (
        <Notification
          type={NotificationType.error}
          className="headset-logo-input-errors"
        >
          {content}
        </Notification>
      );
    }
  }, [errors]);

  useEffect(() => {
    if (inputRef.current && errors) {
      inputRef.current.value = "";
    }
  }, [errors]);

  if (!loading && hidden) {
    return;
  }

  return (
    <div className="headset-logo-input-container">
      {!hidden && <h3>In-Headset Logo</h3>}
      {errorNotification}
      {value ? (
        <div className="headset-logo-input-preview">
          <img src={value} alt="Preview" />
          <Button icon={"fal fa-trash-can"} onClick={() => onChange()} />
        </div>
      ) : (
        <label
          htmlFor="headset-logo-input"
          className="headset-logo-input-label"
        >
          {loading ? (
            <Skeleton rounded height="4rem" width="100%" />
          ) : (
            <Button icon={"fal fa-upload"} onClick={handleClick}>
              Upload Image
            </Button>
          )}
          <span>* .png at 199x26 px.</span>
          <input
            id="headset-logo-input"
            type="file"
            ref={inputRef}
            onChange={handleFileChange}
            disabled={loading}
            accept="image/png"
          />
        </label>
      )}
    </div>
  );
};
